import { type ReactNode, useState } from 'react';
import { Avatar } from '@/components/ui/avatar';

export function pct(n: number, goal: number): number {
  return Math.min(100, Math.max(0, Math.round((n / Math.max(goal, 1)) * 100)));
}

type MarkerProps = {
  label: string;
  name: string;
  photo?: string | undefined;
  progress: number;
  count: number;
  self: boolean;
};

const POP_BASE =
  'pointer-events-none absolute bottom-full left-1/2 z-50 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-surface-elevated px-sm py-1 text-[10px] text-text-secondary shadow-lg transition-all duration-150';

function Popover({
  label,
  count,
  show,
}: {
  label: string;
  count: number;
  show: boolean;
}): ReactNode {
  const vis = show ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0';
  return (
    <div className={`${POP_BASE} ${vis}`}>
      {label}{' '}
      <span className="font-mono tabular-nums text-text-primary">{count}</span>
    </div>
  );
}

export function Marker({
  label,
  name,
  photo,
  progress,
  count,
  self,
}: MarkerProps): ReactNode {
  const [show, setShow] = useState(false);
  const ring = self ? 'ring-1 ring-brand-red' : 'ring-1 ring-white';
  const z = self ? 9999 : count;
  return (
    <div
      className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ${ring}`}
      style={{ left: `${progress}%`, zIndex: z }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <Avatar name={name} size="xxs" src={photo} />
      <Popover label={label} count={count} show={show} />
    </div>
  );
}

export function Track({
  yourPct,
  leaderPct,
}: {
  yourPct: number;
  leaderPct: number | null;
}): ReactNode {
  return (
    <div className="relative h-1.5 w-full rounded-full bg-white/[0.06]">
      {leaderPct !== null && (
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-text-primary/25"
          style={{ width: `${leaderPct}%` }}
        />
      )}
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-brand-red transition-all"
        style={{ width: `${yourPct}%` }}
      />
    </div>
  );
}
